import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';
import axios from 'axios';
import { Trophy, CreditCard, CheckCircle, ArrowLeft, Phone, UploadSimple, TShirt, Ranking, Users } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { RankingTable } from '../components/RankingTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo-tk.png";
const ZELLE_PHONE = "5618596830";

const leagueCategories = [
  { value: 'misto', label: 'Misto' },
  { value: 'open', label: 'Open' },
  { value: 'feminino', label: 'Feminino' },
  { value: 'rei_rainha', label: 'Rei e Rainha' }
];

export default function LeaguePublic() {
  const { t } = useTranslation();
  const leagueCategoriesLocal = [
    { value: 'misto', label: t('league.title') === 'TK League' ? 'Misto' : 'Mixed' },
    { value: 'open', label: 'Open' },
    { value: 'feminino', label: t('league.tabRankingFem') === "Women's Ranking" ? "Women's" : 'Feminino' },
    { value: 'rei_rainha', label: t('league.tabRankingFem') === "Women's Ranking" ? 'King & Queen' : 'Rei e Rainha' }
  ];
  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', categories: [], notes: '' });
  const [loading, setLoading] = useState(false);
  const [regId, setRegId] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [leagueInfo, setLeagueInfo] = useState({ fee: 40, status: 'aberta', label: '$40', uniform_fee: 35 });
  const [uniformForm, setUniformForm] = useState({ name: '', email: '', phone: '', size: 'M' });
  const [uniformStep, setUniformStep] = useState('form');
  const [uniformOrderId, setUniformOrderId] = useState(null);
  const [uploadingUniformReceipt, setUploadingUniformReceipt] = useState(false);
  const [rankingFem, setRankingFem] = useState([]);
  const [rankingMasc, setRankingMasc] = useState([]);
  const [duplas, setDuplas] = useState([]);
  const [duplasFilter, setDuplasFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/league/info`).then(r => setLeagueInfo(r.data)).catch(() => {});
    axios.get(`${API}/league/rankings?gender=feminino`).then(r => setRankingFem(r.data.players || [])).catch(() => {});
    axios.get(`${API}/league/rankings?gender=masculino`).then(r => setRankingMasc(r.data.players || [])).catch(() => {});
    axios.get(`${API}/league/duplas`).then(r => setDuplas(r.data || [])).catch(() => {});
  }, []);

  const duplasByCategory = (duplas || []).reduce((acc, d) => {
    acc[d.category] = acc[d.category] || [];
    acc[d.category].push(d);
    return acc;
  }, {});
  const filteredDuplas = duplasFilter === 'all' ? duplas : (duplas || []).filter(d => d.category === duplasFilter);
  const duplaCategoryLabel = (val) => {
    const f = leagueCategoriesLocal.find(c => c.value === val);
    return f ? f.label : val;
  };

  const handleReceiptUpload = async (file) => {
    if (!file || !regId) return;
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/league/zelle-receipt?registration_id=${regId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Comprovante enviado! Inscricao confirmada.');
      setStep('success');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setUploadingReceipt(false); }
  };

  const handleUniformReceiptUpload = async (file) => {
    if (!file || !uniformOrderId) return;
    setUploadingUniformReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/league/uniform/zelle-receipt?order_id=${uniformOrderId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Comprovante enviado! Pedido confirmado.');
      setUniformStep('success');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setUploadingUniformReceipt(false); }
  };

  const toggleCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(cat) ? prev.categories.filter(c => c !== cat) : [...prev.categories, cat]
    }));
  };

  const handlePayWithCard = async () => {
    setLoading(true);
    try {
      const { data: reg } = await axios.post(`${API}/league/register`, formData);
      const { data: checkout } = await axios.post(`${API}/league/checkout`, { registration_id: reg.id, origin_url: window.location.origin });
      if (checkout.url) { const a = document.createElement('a'); a.href = checkout.url; a.rel = 'noopener'; document.body.appendChild(a); a.click(); document.body.removeChild(a); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); setLoading(false); }
  };

  const handlePayWithZelle = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/league/register`, { ...formData, notes: (formData.notes || '') + ' | Zelle' });
      setRegId(data.id);
      setStep('zelle-info');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setLoading(false); }
  };

  const handleUniformZelle = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/league/uniform`, uniformForm);
      setUniformOrderId(data.id);
      setUniformStep('zelle-info');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setLoading(false); }
  };

  const handleUniformCard = async () => {
    setLoading(true);
    try {
      const { data: order } = await axios.post(`${API}/league/uniform`, uniformForm);
      const { data: checkout } = await axios.post(`${API}/league/uniform/checkout`, {
        order_id: order.id,
        origin_url: window.location.origin
      });
      if (checkout.url) {
        window.location.assign(checkout.url);
      } else {
        toast.error('Nao foi possivel iniciar o pagamento');
        setLoading(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao processar pagamento');
      setLoading(false);
    }
  };

  const isValid = formData.name && formData.email && formData.phone && formData.categories.length > 0;
  const isUniformValid = uniformForm.name && uniformForm.phone;
  const fee = leagueInfo.fee;
  const nCats = formData.categories.length || 1;
  const totalFee = fee * nCats;
  const feeWithCard = (totalFee * 1.05).toFixed(2);
  const uniformFeeWithCard = (35 * 1.05).toFixed(2);
  const isClosed = leagueInfo.status === 'fechada';

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-white border-b border-[#E6E2D8] px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-8 h-8 object-contain" onError={e => { e.target.style.display='none'; }} />
            <span className="font-['Outfit'] font-bold text-[#1A1A1A]">Tia Karol Sports</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login" className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium">{t('common.doLogin')}</Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="rounded-xl overflow-hidden mb-6 max-w-2xl mx-auto">
          <img src="https://customer-assets.emergentagent.com/job_volley-manager-7/artifacts/aqaazmot_31f7f3d4-cdee-4215-aaef-204562c8d85a.jpeg" alt="TK League" className="w-full h-auto object-cover" />
        </div>
        <div className="bg-white border border-[#E6E2D8] rounded-xl p-6 text-center mb-6 max-w-2xl mx-auto">
          <h1 className="font-['Outfit'] text-3xl font-bold text-[#1A1A1A] mb-1">{t('league.title')}</h1>
          <p className="text-[#73706A]">{t('league.subtitle')}</p>
          <p className="text-lg font-bold text-[#E85D04] mt-2 font-['Outfit']">{t('league.nextStage')} 17</p>
        </div>

        <Tabs defaultValue="inscricao">
          <TabsList className="w-full mb-6 flex-wrap h-auto p-1 gap-1">
            <TabsTrigger value="inscricao" className="flex-1 min-w-[110px] text-xs sm:text-sm"><Trophy size={14} className="mr-1 sm:mr-2" /> {t('league.tabRegister')}</TabsTrigger>
            <TabsTrigger value="uniforme" className="flex-1 min-w-[110px] text-xs sm:text-sm"><TShirt size={14} className="mr-1 sm:mr-2" /> {t('league.tabUniform')} $35</TabsTrigger>
            <TabsTrigger value="duplas" className="flex-1 min-w-[110px] text-xs sm:text-sm" data-testid="tab-duplas"><Users size={14} className="mr-1 sm:mr-2" /> {t('league.tabDuplas')}</TabsTrigger>
            <TabsTrigger value="ranking-fem" className="flex-1 min-w-[110px] text-xs sm:text-sm" data-testid="tab-ranking-feminino"><Ranking size={14} className="mr-1 sm:mr-2" /> {t('league.tabRankingFem')}</TabsTrigger>
            <TabsTrigger value="ranking-masc" className="flex-1 min-w-[110px] text-xs sm:text-sm" data-testid="tab-ranking-masculino"><Ranking size={14} className="mr-1 sm:mr-2" /> {t('league.tabRankingMasc')}</TabsTrigger>
          </TabsList>

          {/* INSCRICAO TAB */}
          <TabsContent value="inscricao">
            {step === 'success' && (
              <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center">
                <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-white" weight="fill" />
                </div>
                <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] mb-3">{t('common.confirmed')}</h2>
                <p className="text-[#73706A] mb-6">{t('dropInFem.confirmedSubtitle')}</p>
                <Button onClick={() => { setStep('form'); setFormData({ name: '', email: '', phone: '', categories: [], notes: '' }); }} className="bg-[#E85D04] hover:bg-[#DC2F02] text-white">{t('common.newRegistration')}</Button>
              </div>
            )}

            {step === 'zelle-info' && (
              <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center space-y-6">
                <div className="w-20 h-20 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto"><Phone size={40} className="text-white" /></div>
                <div>
                  <h2 className="font-['Outfit'] text-2xl font-bold">Pague via Zelle</h2>
                  <p className="text-[#73706A] mt-2">Envie <strong className="text-[#E85D04]">${totalFee.toFixed(2)}</strong> via Zelle para:</p>
                </div>
                <div className="bg-[#F5F2EB] rounded-lg p-6">
                  <p className="text-3xl font-bold text-[#1A1A1A] font-['Outfit'] tracking-wider">{ZELLE_PHONE}</p>
                  <p className="text-sm text-[#73706A] mt-2">Tia Karol Sports</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
                  <p className="text-sm text-yellow-800"><strong>Importante:</strong> Envie o comprovante abaixo para confirmar automaticamente!</p>
                </div>
                <label className="cursor-pointer block">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={e => handleReceiptUpload(e.target.files[0])} data-testid="league-zelle-upload" />
                  <Button className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white pointer-events-none" disabled={uploadingReceipt}>
                    {uploadingReceipt ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <UploadSimple size={20} className="mr-2" />}
                    {uploadingReceipt ? 'Enviando...' : 'Enviar Comprovante Zelle'}
                  </Button>
                </label>
              </div>
            )}

            {step === 'form' && (
              <div className="space-y-6">
                {isClosed ? (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
                    <h2 className="font-['Outfit'] text-xl font-bold text-[#9B2226] mb-2">{t('league.registrationsClosedTitle')}</h2>
                    <p className="text-[#73706A]">{t('league.registrationsClosed')}</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-white border border-[#E6E2D8] rounded-xl p-6 text-center">
                      <p className="text-3xl font-bold text-[#E85D04] font-['Outfit']" data-testid="league-total-fee">${totalFee.toFixed(2)}</p>
                      <p className="text-sm text-[#73706A] mt-1">
                        {nCats} {nCats === 1 ? 'categoria' : 'categorias'} x ${fee.toFixed(2)} - {leagueInfo.label}
                      </p>
                      <p className="text-xs text-[#73706A] mt-1">Cartao: ${feeWithCard} (+ 5% taxa) | Zelle: ${totalFee.toFixed(2)}</p>
                      <div className="mt-3 inline-flex flex-wrap items-center justify-center gap-2 text-[10px] text-[#73706A] uppercase">
                        <span className="bg-[#F5F2EB] rounded-full px-2 py-0.5">1 cat = ${fee.toFixed(0)}</span>
                        <span className="bg-[#F5F2EB] rounded-full px-2 py-0.5">2 cats = ${(fee * 2).toFixed(0)}</span>
                        <span className="bg-[#F5F2EB] rounded-full px-2 py-0.5">3 cats = ${(fee * 3).toFixed(0)}</span>
                      </div>
                    </div>

                    <div className="bg-white border border-[#E6E2D8] rounded-xl p-6 space-y-4">
                      <h2 className="font-['Outfit'] font-semibold text-xl text-[#1A1A1A]">{t('dropInFem.regDataTitle')}</h2>
                      <div><Label>{t('common.fullName')} *</Label><Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder={t('common.yourName')} data-testid="league-name" /></div>
                      <div><Label>{t('common.email')} *</Label><Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder={t('login.emailPlaceholder')} data-testid="league-email" /></div>
                      <div><Label>{t('common.phone')} *</Label><Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="(000) 000-0000" data-testid="league-phone" /></div>
                      <div>
                        <Label>{t('league.categories')} *</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {leagueCategoriesLocal.map(cat => (
                            <Button key={cat.value} variant={formData.categories.includes(cat.value) ? 'default' : 'outline'} className={formData.categories.includes(cat.value) ? 'bg-[#E85D04] text-white' : ''} onClick={() => toggleCategory(cat.value)} data-testid={`cat-${cat.value}`}>
                              {cat.label}
                            </Button>
                          ))}
                        </div>
                        {nCats > 0 && (
                          <p className="mt-2 text-sm font-semibold text-[#E85D04]" data-testid="league-categories-summary">
                            {nCats} x ${fee.toFixed(0)} = ${totalFee.toFixed(2)}
                          </p>
                        )}
                      </div>
                      <div><Label>{t('common.notes')}</Label><Input value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder={t('dropInFem.notesPh')} /></div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <Button onClick={handlePayWithCard} disabled={!isValid || loading} className="bg-[#E85D04] hover:bg-[#DC2F02] text-white py-4" data-testid="league-card-btn">
                        {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CreditCard size={20} className="mr-2" /> Cartao ${feeWithCard}</>}
                      </Button>
                      <Button onClick={handlePayWithZelle} disabled={!isValid || loading} variant="outline" className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white py-4" data-testid="league-zelle-btn">
                        <Phone size={20} className="mr-2" /> Zelle ${totalFee.toFixed(2)}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </TabsContent>

          {/* UNIFORME TAB */}
          <TabsContent value="uniforme">
            {uniformStep === 'success' && (
              <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center">
                <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-white" weight="fill" />
                </div>
                <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] mb-3">Pedido Confirmado!</h2>
                <Button onClick={() => { setUniformStep('form'); setUniformForm({ name: '', email: '', phone: '', size: 'M' }); }} className="bg-[#E85D04] hover:bg-[#DC2F02] text-white">Novo Pedido</Button>
              </div>
            )}

            {uniformStep === 'zelle-info' && (
              <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center space-y-6">
                <div className="w-20 h-20 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto"><Phone size={40} className="text-white" /></div>
                <div>
                  <h2 className="font-['Outfit'] text-2xl font-bold">Pague via Zelle</h2>
                  <p className="text-[#73706A] mt-2">Envie <strong className="text-[#E85D04]">$35.00</strong> via Zelle para:</p>
                </div>
                <div className="bg-[#F5F2EB] rounded-lg p-6">
                  <p className="text-3xl font-bold text-[#1A1A1A] font-['Outfit'] tracking-wider">{ZELLE_PHONE}</p>
                </div>
                <label className="cursor-pointer block">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={e => handleUniformReceiptUpload(e.target.files[0])} data-testid="uniform-zelle-upload" />
                  <Button className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white pointer-events-none" disabled={uploadingUniformReceipt}>
                    {uploadingUniformReceipt ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <UploadSimple size={20} className="mr-2" />}
                    {uploadingUniformReceipt ? 'Enviando...' : 'Enviar Comprovante Zelle'}
                  </Button>
                </label>
              </div>
            )}

            {uniformStep === 'form' && (
              <div className="space-y-6">
                <div className="bg-white border border-[#E6E2D8] rounded-xl p-6 text-center">
                  <TShirt size={48} className="text-[#E85D04] mx-auto mb-3" />
                  <h2 className="font-['Outfit'] text-xl font-bold">Uniforme TK League</h2>
                  <p className="text-3xl font-bold text-[#E85D04] font-['Outfit'] mt-2">$35.00</p>
                  <p className="text-xs text-[#73706A]">Cartao: ${uniformFeeWithCard} (+ 5% taxa) | Zelle: $35.00</p>
                </div>

                <div className="bg-white border border-[#E6E2D8] rounded-xl p-6 space-y-4">
                  <div><Label>Nome completo *</Label><Input value={uniformForm.name} onChange={e => setUniformForm({...uniformForm, name: e.target.value})} placeholder="Seu nome" data-testid="uniform-name" /></div>
                  <div><Label>Email</Label><Input value={uniformForm.email} onChange={e => setUniformForm({...uniformForm, email: e.target.value})} placeholder="seu@email.com" /></div>
                  <div><Label>Telefone *</Label><Input value={uniformForm.phone} onChange={e => setUniformForm({...uniformForm, phone: e.target.value})} placeholder="(00) 00000-0000" data-testid="uniform-phone" /></div>
                  <div><Label>Tamanho</Label>
                    <Select value={uniformForm.size} onValueChange={v => setUniformForm({...uniformForm, size: v})}>
                      <SelectTrigger data-testid="uniform-size"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PP">PP</SelectItem>
                        <SelectItem value="P">P</SelectItem>
                        <SelectItem value="M">M</SelectItem>
                        <SelectItem value="G">G</SelectItem>
                        <SelectItem value="GG">GG</SelectItem>
                        <SelectItem value="XG">XG</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Button onClick={handleUniformCard} disabled={!isUniformValid || loading} className="bg-[#E85D04] hover:bg-[#DC2F02] text-white py-4" data-testid="uniform-card-btn">
                    {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CreditCard size={20} className="mr-2" /> Cartao ${uniformFeeWithCard}</>}
                  </Button>
                  <Button onClick={handleUniformZelle} disabled={!isUniformValid || loading} variant="outline" className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white py-4" data-testid="uniform-zelle-btn">
                    <Phone size={20} className="mr-2" /> Zelle $35.00
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* RANKING FEMININO */}
          <TabsContent value="duplas">
            <div className="bg-white border border-[#E6E2D8] rounded-xl p-6" data-testid="duplas-tab-content">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <h2 className="font-['Outfit'] font-semibold text-xl text-[#1A1A1A] flex items-center gap-2">
                    <Users size={22} className="text-[#E85D04]" /> Duplas Confirmadas
                  </h2>
                  <p className="text-sm text-[#73706A]">Veja as duplas oficiais por categoria.</p>
                </div>
                <div className="flex items-center gap-1 bg-[#F5F2EB] rounded-lg p-1 flex-wrap">
                  <button
                    onClick={() => setDuplasFilter('all')}
                    className={`px-3 py-1.5 rounded text-xs font-medium ${duplasFilter === 'all' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}
                    data-testid="duplas-filter-all"
                  >
                    Todas ({duplas.length})
                  </button>
                  {leagueCategories.map(cat => {
                    const count = (duplasByCategory[cat.value] || []).length;
                    if (count === 0) return null;
                    return (
                      <button
                        key={cat.value}
                        onClick={() => setDuplasFilter(cat.value)}
                        className={`px-3 py-1.5 rounded text-xs font-medium ${duplasFilter === cat.value ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}
                        data-testid={`duplas-filter-${cat.value}`}
                      >
                        {cat.label} ({count})
                      </button>
                    );
                  })}
                </div>
              </div>

              {filteredDuplas.length === 0 ? (
                <div className="text-center py-12 text-[#73706A]">
                  <Users size={40} className="mx-auto mb-2 opacity-40" />
                  <p>{duplas.length === 0 ? 'Nenhuma dupla confirmada ainda.' : 'Nenhuma dupla nesta categoria.'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(filteredDuplas.reduce((acc, d) => {
                    acc[d.category] = acc[d.category] || [];
                    acc[d.category].push(d);
                    return acc;
                  }, {})).map(([cat, list]) => (
                    <div key={cat}>
                      <h3 className="text-sm font-semibold text-[#E85D04] uppercase tracking-wide mb-2">
                        {duplaCategoryLabel(cat)}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {list.map((d, i) => (
                          <div
                            key={d.id}
                            className="flex items-center gap-3 p-3 bg-[#F5F2EB] rounded-lg"
                            data-testid={`dupla-card-${d.id}`}
                          >
                            <div className="w-8 h-8 rounded-full bg-[#E85D04] text-white flex items-center justify-center text-xs font-bold">
                              {i + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#1A1A1A]">
                                <strong>{d.player1}</strong>
                              </p>
                              <p className="text-sm text-[#1A1A1A]">
                                <strong>{d.player2}</strong>
                              </p>
                              {d.notes && (
                                <p className="text-[10px] text-[#73706A] mt-1 truncate">{d.notes}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="ranking-fem">
            <RankingTable rows={rankingFem} genderLabel="Feminino" crownLabel="Rainha" />
          </TabsContent>

          {/* RANKING MASCULINO */}
          <TabsContent value="ranking-masc">
            <RankingTable rows={rankingMasc} genderLabel="Masculino" crownLabel="Rei" />
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-[#73706A] hover:text-[#1A1A1A] flex items-center justify-center gap-2"><ArrowLeft size={16} /> {t('common.back')}</Link>
        </div>
      </main>
    </div>
  );
}
